import React, { useEffect, useRef } from "react";
import FormCheck, { FormCheckProps } from "react-bootstrap/FormCheck";
import styled from "styled-components/macro";
import Tooltip, { TooltipProps } from "ui/Tooltip";
import { theme } from "ui/theme";
import useUniqueId from "helpers/useUniqueId";

export interface CheckboxProps extends FormCheckProps {
  /**
   * Besides being checked or unchecked, checkboxes can also be "indeterminate",
   * meaning neither toggled on nor off.
   */
  indeterminate?: boolean;
  disabledTooltipProps?: Omit<TooltipProps, "children">;
}

const $Checkbox = styled(FormCheck).attrs({
  type: "checkbox",
})`
  zoom: 1.25;
`;

const $DisabledCheckboxTooltip = styled(Tooltip)`
  background-color: ${theme.ColorSpace500};
  border: none;
  border-shadow: none;
  > * {
    color: ${theme.ColorWhite};
  }
`;

export default function Checkbox({
  indeterminate,
  disabledTooltipProps,
  ...props
}: CheckboxProps) {
  const checkboxRef = useRef<HTMLInputElement>(null);

  /**
   * There is no `indeterminate` HTML attribute. The value must be set using JS
   * to modify the `HTMLInputElement` object's `indeterminate` property.
   *
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/checkbox#indeterminate_state_checkboxes
   * https://github.com/facebook/react/issues/1798
   *
   * From a UI perspective, "indeterminate" is a third state (along with
   * "checked" and "unchecked"). Programmatically, however, it's a parallel
   * state since the `checked` and `indeterminate` properties are separate.
   *
   * Note that we intentionally run the effect on every render to manually
   * reconcile values.
   */
  useEffect(() => {
    const element = checkboxRef.current;
    if (element && element.indeterminate !== indeterminate) {
      element.indeterminate = !!indeterminate;
    }
  });

  const spanId = useUniqueId();

  if (props.disabled && disabledTooltipProps) {
    return (
      <>
        <span id={spanId} className="visually-hidden">
          {disabledTooltipProps?.label}
        </span>
        <$DisabledCheckboxTooltip {...disabledTooltipProps}>
          <span>
            <$Checkbox {...props} ref={checkboxRef} aria-describedby={spanId} />
          </span>
        </$DisabledCheckboxTooltip>
      </>
    );
  }
  return <$Checkbox {...props} ref={checkboxRef} />;
}
